import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { KeyRound, Loader2, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "../../components/auth/AuthLayout";
import { ResendCountdown } from "../../components/auth/ResendCountdown";
import { OtpBoxes } from "../../components/auth/OtpBoxes";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { authApi, errorMessage } from "../../lib/api";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

// Password recovery by a one-time EMAIL code, then set a new password.
// (Phone/SMS recovery is disabled until MSG91 is approved — email only.)
export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const expired = !!location.state?.expired;
  const [step, setStep] = useState("request"); // request | verify | reset
  const [req, setReq] = useState(null);         // { reset_id, masked }
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e?.preventDefault();
    const id = email.trim();
    if (!isEmail(id)) return toast.error("Enter the email address on your account");
    setBusy(true);
    try {
      const r = await authApi.passwordResetRequest(id);
      setReq(r); setStep("verify"); setOtp("");
      toast.success(`If an account matches, a code was sent to ${r.masked || id}`);
    } catch (err) { toast.error(errorMessage(err, "Could not send the code")); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.length < 4) return toast.error("Enter the code we sent you");
    setBusy(true);
    try {
      const data = await authApi.passwordResetVerify({ reset_id: req.reset_id, otp: otp.trim() });
      setResetToken(data.reset_token); setStep("reset");
      toast.success("Code verified — set a new password");
    } catch (err) { toast.error(errorMessage(err, "Invalid code")); } finally { setBusy(false); }
  };

  const autoVerifyRef = useRef("");
  useEffect(() => {
    if (step === "verify" && otp.length === 6 && !busy && autoVerifyRef.current !== otp) { autoVerifyRef.current = otp; verify(); }
  }, [otp, step, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await authApi.passwordResetComplete({ reset_token: resetToken, new_password: pwd });
      toast.success("Password changed. Please sign in.");
      navigate("/auth/login", { replace: true });
    } catch (err) { toast.error(errorMessage(err, "Could not change password")); } finally { setBusy(false); }
  };

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="No problem — we'll email a one-time code to your account, then you can set a new password."
      footer={<Link to="/auth/login" className="inline-flex items-center gap-1 font-semibold text-brand hover:underline" data-testid="forgot-back-login"><ArrowLeft className="h-3.5 w-3.5" /> Back to log in</Link>}
    >
      {step === "request" ? (
        <form onSubmit={send} className="space-y-5">
          {expired && <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="forgot-expired-note">For your security, passwords expire every 30 days. Reset yours with the email code below to continue.</div>}
          <div className="space-y-2">
            <Label htmlFor="fp-id">Email address</Label>
            <Input id="fp-id" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11" autoFocus data-testid="forgot-identifier" />
            <p className="flex items-center gap-1.5 text-xs text-ink-muted" data-testid="forgot-channel-note"><Mail className="h-3.5 w-3.5" /> We'll email your recovery code from no-reply@kohimataxi.com.</p>
          </div>
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="forgot-send">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}Send recovery code</Button>
        </form>
      ) : step === "verify" ? (
        <form onSubmit={verify} className="space-y-5">
          <p className="text-sm text-ink-soft" data-testid="forgot-sent-to">Code sent <Mail className="inline h-3.5 w-3.5" /> to <span className="font-semibold text-ink">{req?.masked || email}</span>. <button type="button" onClick={() => { setStep("request"); setReq(null); }} className="font-semibold text-brand hover:underline" data-testid="forgot-change">Change</button></p>
          <p className="text-xs text-ink-muted" data-testid="forgot-spam">Not in your inbox? Check your spam or junk folder.</p>
          <div className="space-y-2">
            <Label htmlFor="fp-otp">6-digit code</Label>
            <OtpBoxes value={otp} onChange={(v) => setOtp(v)} autoFocus testId="forgot-otp" />
            <p className="text-center text-xs text-ink-muted" data-testid="forgot-paste-hint">Tip: you can paste the code — it verifies automatically.</p>
          </div>
          <Button type="submit" disabled={busy || otp.length < 4} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="forgot-verify">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify code</Button>
          <ResendCountdown onResend={() => send()} busy={busy} nonce={req?.reset_id} testId="forgot-resend" />
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="fp-pwd">New password</Label>
            <Input id="fp-pwd" type="password" minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 6 characters" className="h-11" autoFocus data-testid="forgot-new-password" />
          </div>
          <Button type="submit" disabled={busy || pwd.length < 6} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="forgot-reset-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Set new password</Button>
        </form>
      )}
    </AuthLayout>
  );
}
